import { EditLabelDialog, getLabelDialogTitle, getLabelTypeConfig, isRestrictedFieldSet } from '@tableau/catalog-ui-temp';
import React from 'react';
import { SagaIterator } from 'redux-saga';
import { call, put } from 'redux-saga/effects';

import { SetLabelValueAction } from '~src/catalog/actions/labels/SetLabelValueAction';
import { CatalogProviders } from '~src/catalog/CatalogProviders';
import { CatalogTelemetryProvider } from '~src/catalog/DataCatalogTelemetry';
import { ILabelValueInfo } from '~src/catalog/models/ILabelValueInfo';
import { getItemsWithCorrectLuids } from '~src/Collections/sagas/CollectionSagas';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';

import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../../codegen/Localize';

/**
 * Unified saga handler for editing label values (release 262).
 * Replaces duplicate label dialog implementations with catalog-ui's EditLabelDialog,
 * using LabelTypeConfig to determine which fields are editable per label type.
 */
export function* handleSetLabelValue(action: SetLabelValueAction): SagaIterator {
  try {
    yield call(openLabelValueDialog, action);
    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.item]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

function openLabelValueDialog(action: SetLabelValueAction) {
  const itemWithCorrectLuid = getItemsWithCorrectLuids([action.item])[0];
  const { contentType } = itemWithCorrectLuid;

  if (contentType !== 'labelValue') {
    throw new Error(`User cannot change label value contents on ${contentType}.`);
  }

  const item: ILabelValueInfo = action.item;
  const config = getLabelTypeConfig(item.value, item.category);

  // Restricted (compact) editing is a built-in-label concept only: Certification
  // and AI edit description-only, monitoring warnings edit description+visibility.
  // Custom labels (builtIn === false) are the one type that owns an editable
  // category, so they must always get the full two-column layout with the
  // category sidebar. Passing editableFields for a custom label would collapse
  // that sidebar, so we gate on item.builtIn and omit editableFields otherwise.
  //
  // isRestrictedFieldSet mirrors the dialog's own layout rule (a strict subset of
  // the renderable value fields), keeping this decision in lockstep with the
  // catalog-ui component that consumes it.
  const isRestricted = item.builtIn && isRestrictedFieldSet(config.editableFields);
  const editableFields = isRestricted ? config.editableFields : undefined;
  const dialogTitle = getLabelDialogTitle(item.value);

  return ReactModalService.openCustom(({ close }) => {
    return (
      <CatalogProviders>
        <CatalogTelemetryProvider>
          <EditLabelDialog
            close={close}
            dismiss={close}
            labelValueName={item.value}
            categoryName={item.category}
            // Handle optional description (labelDescription can be null/undefined from API)
            description={item.labelDescription || ''}
            // Handle null/undefined visibility
            elevatedDefault={item.visiblity || false}
            builtIn={item.builtIn}
            editableFields={editableFields}
            dialogTitle={dialogTitle}
          />
        </CatalogTelemetryProvider>
      </CatalogProviders>
    );
  }).result;
}
